import rootRoutes from './rootRoutes'
import Router from './Router'

/**
 * The slice of the router's history object this de-duplication shim drives.
 *
 * Written out locally rather than imported, because neither available source is
 * correct here: react-router-dom@5 passes a **history@4** instance, whose
 * `listen` callback is invoked as `(location, action)`, but `import 'history'`
 * resolves to **history@5**'s types (a single `Update` argument), and
 * @types/react-router's own `H.History` alias resolves there too — so it
 * degrades to `any`. Stating the contract keeps `listen`'s callback and
 * `lastLocation` fully type-checked instead of casting the shape away.
 *
 * `Routes.test.tsx` drives both members, so an actual signature change fails the
 * suite even where the compiler cannot see it.
 */
type RouterLocation = {
  pathname: string
  search: string
  hash: string
  state?: unknown
}
export type RouterHistory = {
  push: (pathname: string, state?: unknown) => void
  listen: (listener: (location: RouterLocation) => void) => void
}

let lastLocation: RouterLocation | null = null

export const handleHistoryPush =
  (prevHistoryPush: RouterHistory['push']) =>
  (pathname: string, state: unknown = {}) => {
    if (
      lastLocation === null ||
      pathname !==
        `${lastLocation.pathname}${lastLocation.search}${lastLocation.hash}` ||
      JSON.stringify(state) !== JSON.stringify(lastLocation.state)
    ) {
      prevHistoryPush(pathname, state)
    }
  }

export const HandlePageConditionalRender = ({
  history,
  component,
}: {
  history: RouterHistory
  component?: React.ReactNode
}): React.JSX.Element => {
  // prevent react router push same route to history stack
  history.listen((location) => {
    lastLocation = location // oxlint-disable-line react/globals
  })
  const prevHistoryPush = history.push
  history.push = handleHistoryPush(prevHistoryPush) // oxlint-disable-line react/immutability

  return <>{component}</>
}

const Routes = () => <Router routes={rootRoutes} />

export default Routes
